// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Trash from "@insite/mobius/Icons/Trash";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getNotifyStyle } from "@rpsc/App/Components/Notify";
import xProductPartNumbersStyles from "@rpsc/App/SharedStyles/ProductPartNumbersStyles";
import { StyledTypography } from "@rpsc/MobiusOverrides/Link/Link";
import { TooltipPresentationProps } from "@rpsc/MobiusOverrides/Tooltip/Tooltip";
import { ProductPriceStyles } from "@rpsc/Overrides/Components/ProductPrice";
import AddToList from "@rpsc/XDL/Icons/AddToList";
import IconPencilEdit from "@rpsc/XDL/Icons/IconPencilEdit";
import IconPlus from "@rpsc/XDL/Icons/IconPlus";
import IconTrash from "@rpsc/XDL/Icons/IconTrash";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    lineNotesLinkAdd?: LinkPresentationProps;
    lineNotesLinkHide?: LinkPresentationProps;
    productBrand?: ProductBrandStyles;
    badgesContainer?: InjectableCss;
    actionsWrapper?: InjectableCss;
    totalWrapper?: InjectableCss;
    warningToolTip?: TooltipPresentationProps;
    alertWarningIconStyles?: IconProps;
    infoWrapper?: InjectableCss;
    infoText?: TypographyPresentationProps;
    infoHeading?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    totalPrice?: SmallHeadingAndTextStyles;
}

const iconsSharedStyles: any = {
    icon: {
        iconProps: {
            size: 11,
            css: css`
                &,
                & > svg {
                    height: 13px;
                }
            `,
        },
    },
};

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 91px;
                height: 91px;
                background-color: ${color("common.white")};
                justify-content: center;
                border: 1px solid ${color("common.border")};
                border-radius: 2px;
                overflow: hidden;
                &&& > img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                }
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            mergeCss: true,
            color: "text",
            typographyProps: {
                size: 14,
                weight: fontConfig.weight.bold,
                lineHeight: "19px",
            },
            css: css`
                display: inline-block;
                align-items: flex-start;
                -webkit-box-align: start;
                -webkit-align-self: flex-start;
                align-self: flex-start;
                margin-bottom: 8px;
                height: auto;
                line-height: 19px;
                ${ellipsis(2)}
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 19px;
                height: auto;
                margin-bottom: 24px;

                ${xScreen("lte", "md")} {
                    margin-bottom: 8px;
                }
            `,
        },
    },
    productBrand: {
        nameLink: {
            mergeCss: true,
            css: css`
                display: flex;
                ${StyledTypography} {
                    font-weight: ${fontConfig.weight.bold};
                    font-size: 12px;
                }
            `,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                ${xScreen("lte", "md")} {
                    > div {
                        gap: 0 22px;
                    }
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto;
                word-break: keep-all;
            `,
        },
    },
    lineNotesLinkAdd: {
        ...createLinkIconStyles(IconPencilEdit, {
            ...iconsSharedStyles,
        }),
    },
    addToListLink: {
        ...createLinkIconStyles(AddToList, {
            ...iconsSharedStyles,
        }),
    },
    lineNotesLinkHide: {
        ...createLinkIconStyles(IconPlus, {
            ...iconsSharedStyles,
        }),
    },
    productPrice: {
        wrapper: {
            css: css`
                flex-wrap: wrap;
                ${xScreen("lte", "md")} {
                    flex-direction: column;
                    align-items: start;
                }
            `,
        },
        priceLabelText: {
            size: 12,
            weight: fontConfig.weight.regular,
            lineHeight: "17px",
            css: css`
                text-transform: capitalize;
                padding: 0;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    &&& {
                        display: block;
                        font-size: 14px;
                        line-height: 19px;
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                line-height: 19px;
            `,
        },
        price: {
            priceText: {
                size: 14,
                weight: fontConfig.weight.bold,
                lineHeight: "19px",
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                    weight: fontConfig.weight.regular,
                    color: "common.black",
                    lineHeight: "14px",
                },
            },
            callForPriceText: {
                variant: "h6",
                forwardAs: "span",
            },
        },
    },
    extendedUnitNetPriceHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    line-height: 19px;
                    width: auto;
                    @media print {
                        margin-left: 20px;
                    }
                `,
            },
            heading: {
                size: 12,
                lineHeight: "14px",
                weight: fontConfig.weight.regular,
                css: css`
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        display: block;
                    }
                `,
            },
            text: {
                size: 14,
                weight: fontConfig.weight.bold,
                lineHeight: "19px",
                color: "common.black",
                css: css`
                    text-align: start;
                    display: flex;
                    ${xScreen("lte", "md")} {
                        padding-top: 0;
                    }
                `,
            },
        },
    },
    quantityTextField: {
        css: css`
            border: 1px solid ${color("common.ui4")};
        `,
    },

    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionNameText: {
        ...xProductPartNumbersStyles.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: fontConfig.weight.regular,
        lineHeight: "17px",
        css: css`
            margin-bottom: 8px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
        `,
    },
    removeCartLineIcon: {
        src: IconTrash,
        size: 16,
        color: "text.link",
        css: css`
            & svg:hover {
                color: ${color("common.ui8")};
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                margin-top: 8px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            &&& {
                margin-top: 12px;
            }
            > ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    productAvailability: {
        messageGridItem: {
            width: 12,
        },
        availabilityByWarehouseLinkGridItem: {
            width: 12,
        },
        availabilityByWarehouseLink: {
            css: css`
                margin-left: unset;
            `,
        },
        container: {
            offsetCss: css`
                flex-direction: column;
                margin-top: 4px;
                ${xScreen("lte", "md")} {
                    gap: 4px;
                }
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: fontConfig.weight.regular,
        color: "common.black",
        css: css`
            line-height: 14px;
        `,
    },
    badgesContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 8px;
            & > div:empty {
                display: none;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 12px;
        `,
    },

    alertWarningIconStyles: {
        size: 14,
        color: "warning",
    },

    totalWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
            ${xScreen("lte", "md")} {
                align-items: end;
            }
        `,
    },

    warningToolTip: {
        mergeCss: true,
        iconProps: {
            size: 14,
        },
        cssOverrides: {
            tooltipClickable: css`
                margin: 0;
            `,
            tooltipWrapper: css`
                margin-bottom: 2px;
            `,
            tooltipContainer: css`
                min-width: 256px;
            `,
        },
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    infoText: {
        size: 14,
        lineHeight: "19px",
    },
    infoHeading: {
        size: 12,
        lineHeight: "17px",
        weight: fontConfig.weight.bold,
        color: "common.black",
        css: css`
            display: inline-block;
            margin-bottom: 4px;
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    totalPrice: {
        extendedStyles: {
            wrapper: {
                css: css`
                    line-height: 19px;
                    width: auto;
                    @media print {
                        margin-left: 20px;
                    }
                `,
            },
            heading: {
                size: 12,
                lineHeight: "17px",
                weight: fontConfig.weight.regular,
                css: css`
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        display: block;
                    }
                `,
            },
            text: {
                size: 14,
                weight: fontConfig.weight.bold,
                lineHeight: "19px",
                color: "common.black",
                css: css`
                    text-align: start;
                    display: flex;
                    ${xScreen("lte", "md")} {
                        padding-top: 0;
                        font-size: 18px;
                    }
                `,
            },
        },
    },
};

export default cartLineSharedStyles;
